import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  IconButton, 
  useTheme, 
  useMediaQuery,
  Tooltip,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut,
  Scale,
  Sun,
  Moon,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { SideNav } from './layout/SideNav';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeStore();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    setIsDrawerCollapsed(!isDrawerCollapsed);
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: '100%',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
                mr: 2,
              }}
            >
              <Scale 
                size={isMobile ? 22 : 26}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }}
              />
            </Box>
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                letterSpacing: '-0.02em',
                background: 'linear-gradient(to right, #fff, rgba(255,255,255,0.85))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              LegalCo
            </Typography>
          </Box>
          {user && !isMobile && (
            <Typography 
              variant="body1" 
              sx={{ 
                mr: 2,
                fontWeight: 500,
                opacity: 0.9,
              }}
            >
              {user.name}
            </Typography>
          )}
          <IconButton 
            color="inherit" 
            onClick={toggleTheme} 
            sx={{ mr: 1 }}
          >
            {mode === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </IconButton>
          <IconButton 
            color="inherit" 
            onClick={logout}
          >
            <LogOut size={20} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ 
          width: { md: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth }, 
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '100%', // Full width on mobile
              maxWidth: drawerWidth,
              padding: '0 8px',
              borderRight: 'none',
              background: theme.palette.mode === 'light' 
                ? '#ffffff'
                : '#262626',
              boxShadow: theme.palette.mode === 'light' 
                ? '4px 0 24px rgba(0, 0, 0, 0.03)'
                : '4px 0 24px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
            <IconButton onClick={handleDrawerToggle} edge="end">
              <X size={20} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <SideNav
            isCollapsed={false}
            onCollapse={handleDrawerCollapse}
            onNavigate={(path) => {
              navigate(path);
              setMobileOpen(false);
            }}
          />
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth,
              padding: '0 8px',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          <Toolbar />
          <SideNav
            isCollapsed={isDrawerCollapsed}
            onCollapse={handleDrawerCollapse}
            onNavigate={navigate}
          />
        </Drawer>
      </Box>

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 3, md: 4 },
          width: { 
            xs: '100%', 
            md: `calc(100% - ${isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth}px)` 
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};